/*
  Tearstones announcement playback

  Owns notice rotation, complete overflow passes, reading pauses and section lifecycle.
  Native destination links and manually readable copy remain independent of playback.
*/
if (!customElements.get('tearstones-notices')) {
  customElements.define('tearstones-notices', class extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.items = [...this.querySelectorAll('[data-notice-item]')];
      this.pauseButton = this.querySelector('[data-notice-pause]');
      if (!this.items.length || !this.pauseButton) return;

      this.events = new AbortController();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // The merchant explicitly enables moving overflow notices; visitors can pause them.
      this.motionOptIn = true;
      this.paused = false;
      this.hovered = false;
      this.focused = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
      this.visible = !('IntersectionObserver' in window);
      const options = { signal: this.events.signal };

      this.pauseButton.addEventListener('click', () => {
        this.paused = !this.paused;
        if (this.motion.matches) {
          this.motionOptIn = !this.paused;
          this.measure();
          return;
        }
        this.sync();
      }, options);
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.sync();
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.sync();
      }, options);
      this.addEventListener('focusin', (event) => {
        this.focused = event.target.matches(':focus-visible');
        if (this.focused) {
          if (this.animation) {
            this.animation.currentTime = 0;
            this.completed = false;
          }
          this.stopTimer();
          this.viewport.scrollLeft = 0;
        }
        this.sync();
      }, options);
      this.addEventListener('focusout', () => {
        const signal = this.events.signal;
        queueMicrotask(() => {
          if (signal.aborted || !this.isConnected) return;
          this.focused = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
          if (!this.focused) this.viewport.scrollLeft = 0;
          this.sync();
        });
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => {
        this.motionOptIn = !this.paused;
        this.measure();
      }, options);
      window.addEventListener('resize', () => this.queueMeasure(), options);
      document.fonts?.addEventListener('loadingdone', () => this.queueMeasure(), options);
      const signal = this.events.signal;
      document.fonts?.ready.then(() => {
        if (!signal.aborted && this.isConnected) this.queueMeasure();
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (!signal.aborted) this.queueMeasure();
        });
        this.items.forEach((item) => {
          this.resizeObserver.observe(item);
          this.resizeObserver.observe(item.querySelector('[data-notice-copy]'));
        });
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          if (signal.aborted) return;
          this.visible = entry.isIntersecting;
          this.sync();
        });
        this.observer.observe(this);
      }
      this.show(0);
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      this.events?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.frame);
      this.frame = undefined;
      this.stopTimer();
      this.animation?.cancel();
      this.animation = undefined;
      this.distance = undefined;
      this.completed = false;
      if (this.duplicate) this.duplicate.hidden = true;
      if (this.pauseButton) this.pauseButton.hidden = true;
      this.dataset.scrolling = 'false';
      this.dataset.reading = 'true';
      this.dataset.motionOptIn = 'false';
    }

    show(index) {
      this.stopTimer();
      this.animation?.cancel();
      this.animation = undefined;
      this.distance = undefined;
      this.completed = false;
      this.remaining = 5000;
      this.activeIndex = index;
      this.items.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.hidden = !active;
        item.inert = !active;
        item.setAttribute('aria-hidden', String(!active));
        item.setAttribute('tabindex', active ? '0' : '-1');
        item.scrollLeft = 0;
        item.querySelector('[data-notice-duplicate]').hidden = true;
      });
      this.viewport = this.items[index];
      this.track = this.viewport.querySelector('[data-notice-track]');
      this.copy = this.viewport.querySelector('[data-notice-copy]');
      this.duplicate = this.viewport.querySelector('[data-notice-duplicate]');
      this.measure();
    }

    advance() {
      if (!this.canPlay()) {
        this.completed = true;
        return;
      }
      this.show((this.activeIndex + 1) % this.items.length);
    }

    queueMeasure() {
      if (this.frame !== undefined) return;
      const signal = this.events.signal;
      const frame = window.requestAnimationFrame(() => {
        if (signal.aborted || this.frame !== frame) return;
        this.frame = undefined;
        if (this.isConnected) this.measure();
      });
      this.frame = frame;
    }

    measure() {
      const width = this.copy.getBoundingClientRect().width;
      const available = this.viewport.clientWidth;
      const overflow = available > 0 && width > available + 1;
      const canAnimate = overflow && typeof this.track.animate === 'function';
      const canScroll = canAnimate && (!this.motion.matches || this.motionOptIn);
      this.dataset.overflow = String(overflow);
      this.dataset.scrolling = String(canScroll);
      this.dataset.motionOptIn = String(this.motionOptIn);
      this.duplicate.hidden = !canScroll;
      this.pauseButton.hidden = this.items.length < 2 && !canAnimate;

      if (!canScroll) {
        if (this.animation) this.remaining = 5000;
        this.animation?.cancel();
        this.animation = undefined;
        this.distance = undefined;
        this.completed = false;
      } else {
        const gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
        const distance = width + gap;
        if (!this.animation || this.distance !== distance) {
          this.stopTimer();
          this.animation?.cancel();
          this.completed = false;
          this.viewport.scrollLeft = 0;
          this.distance = distance;
          const animation = this.track.animate([
            { transform: 'translateX(0)' },
            { transform: `translateX(-${distance}px)` },
          ], { duration: distance / 28 * 1000, iterations: this.items.length > 1 ? 1 : Infinity, easing: 'linear' });
          this.animation = animation;
          const signal = this.events.signal;
          animation.onfinish = () => {
            if (!signal.aborted && this.isConnected && this.animation === animation) this.advance();
          };
          animation.pause();
        }
      }
      this.sync();
    }

    canPlay() {
      return this.isConnected && this.visible && !document.hidden
        && !this.paused && !this.hovered && !this.focused;
    }

    stopTimer() {
      if (this.timer !== undefined) {
        window.clearTimeout(this.timer);
        this.remaining = Math.max(0, this.remaining - (window.performance.now() - this.startedAt));
      }
      this.timer = undefined;
      this.timerToken = undefined;
    }

    startTimer() {
      if (this.timer !== undefined || this.items.length < 2) return;
      this.startedAt = window.performance.now();
      const token = {};
      const signal = this.events.signal;
      this.timerToken = token;
      this.timer = window.setTimeout(() => {
        if (signal.aborted || !this.isConnected || this.timerToken !== token) return;
        this.timer = undefined;
        this.remaining = 0;
        this.advance();
      }, this.remaining);
    }

    sync() {
      this.dataset.reading = String(this.focused || !this.animation);
      const playing = this.canPlay();
      if (this.completed && playing) {
        this.advance();
        return;
      }
      if (this.animation) {
        if (playing) this.animation.play();
        else this.animation.pause();
      }
      if (playing && !this.animation && (this.motion.matches || this.dataset.overflow !== 'true')) this.startTimer();
      else this.stopTimer();
      const stopped = this.paused;
      const resumeLabel = this.motion.matches ? this.dataset.motionResumeLabel : this.dataset.resumeLabel;
      this.pauseButton.setAttribute('aria-label', stopped ? resumeLabel : this.dataset.pauseLabel);
      this.pauseButton.setAttribute('aria-pressed', String(stopped));
      this.pauseButton.dataset.paused = String(stopped);
      this.querySelector('[data-notice-pause-icon]').hidden = stopped;
      this.querySelector('[data-notice-play-icon]').hidden = !stopped;
    }
  });
}
